import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import FeedbackIcon from '@mui/icons-material/Feedback';
import { submitFeedback } from '../api/client';
import { useTranslation } from 'react-i18next';

const FeedbackDialog = ({ 
  open, 
  onClose, 
  workoutData,
  gpxSegments,
  fitChunks,
  parameters,
  fitFile,
  gpxFile,
  selectedGpxSegment,
  currentPage
}) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    // Validate message
    if (message.trim().length < 10) {
      setError(t('feedbackMinimumError'));
      return;
    }

    // Check if analysis session ID exists

    
    if (!workoutData?.analysis_session_id) {
      setError(t('noAnalysisSessionError'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await submitFeedback({
        message: message.trim(),
        analysisSessionId: workoutData.analysis_session_id
      });
      
      setMessage('');
      onClose();
      alert(t('feedbackSubmittedSuccess'));
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error detail array:', JSON.stringify(error.response?.data?.detail, null, 2));
      
      // Format error message
      let errorMessage = t('feedbackSubmitError');
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // FastAPI validation errors
          errorMessage = detail.map(err => err.msg).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setMessage('');
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '400px'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <FeedbackIcon />
          <Typography variant="h6">{t('feedbackDialogTitle')}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField
            multiline
            rows={8}
            fullWidth
            placeholder={t('feedbackPlaceholder')}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={isSubmitting}
            autoFocus
            inputProps={{
              maxLength: 5000
            }}
            helperText={t('charactersMinimum', { count: message.length })}
          />
          
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Alert severity="info" variant="outlined">
            <Typography variant="body2" fontWeight="bold" mb={1}>
              {t('feedbackIncludesTitle')}
            </Typography>
            <Typography variant="body2" component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
              <li>{t('feedbackIncludeMessage')}</li>
              <li>{t('feedbackIncludeWorkoutData')}</li>
              <li>{t('feedbackIncludeFiles')}</li>
              <li>{t('feedbackIncludePageState')}</li>
            </Typography>
            <Typography variant="body2" mt={1} fontStyle="italic">
              {t('feedbackIncludeHelp')}
            </Typography>
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: 2 }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          {t('cancel')}
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={isSubmitting || message.trim().length < 10}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : <FeedbackIcon />}
        >
          {isSubmitting ? t('submitting') : t('submitFeedback')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackDialog;
